import { MRTGrid, NumberWithUnit } from "@foodpilot/foods";
import { Typography, useTheme, useMediaQuery, Stack, Box } from "@mui/material";
import { useNavigate } from "react-router";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import {
    ActionPlanActionList,
    useGetAllActionPlanActions,
} from "src/api/client-api/ActionPlan/ActionPlanAction/useGetAllActionPlanAction";
import { useDeleteActionPlanAction } from "src/api/client-api/ActionPlan/ActionPlanAction/useDeleteActionPlanAction";
import { useActionsListContext } from "../ActionsListContext";
import { useTranslation } from "react-i18next";
import { getScorePrecision } from "src/context/Scores/provider";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { Suspense } from "src/components/utils/Suspense";
import { ActionCard } from "./ActionCard";
import { ApproximateImpactTooltip } from "src/components/Actions/Common/ApproximateImpactTooltip";
import { isAppInPDFMode } from "src/utils/rendering";

export type ActionGridData = {
    id: string;
    actionId: number;
    order: number;
    title: string;
    categories: {
        id: number;
        title: string;
    }[];
    startYear?: string;
    endYear?: string;
    status: string;
    impact: number | null;
    impactsByScore: Record<number, number | null>;
    unit: string;
    canEdit: boolean;
    canDelete: boolean;
    isEngaged: boolean; // Si l'action est dans le plan
};

export type ActionsGridProps = {
    allowDelete?: boolean;
};

export const ActionsGrid = (props: ActionsGridProps) => {
    const { allowDelete = true } = props;
    const { lastSelectedVariation } = useEntityScores();
    const {
        isLoading: isQueryLoading,
        areActionsReady,
        actionPlan,
        isFetched,
        olapScoresQuery,
        scoresEnabled,
    } = useActionsContext();
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isPdfMode = isAppInPDFMode();
    const navigate = useNavigate();
    const deleteActionMutation = useDeleteActionPlanAction();
    const { formatScore, getColor, selectedScorePrecision, scoresWithVariations } = useEntityScores();

    // Validate required parameters
    const actionPlanId = actionPlan?.id;

    const { data: allActionPlanActions } = useGetAllActionPlanActions({
        actionPlanID: actionPlan?.id,
        scoresQuery: olapScoresQuery,
    });

    const { mrt_columns: gridColumns } = useActionsListContext();

    const scoreId = lastSelectedVariation.scoreId;

    const actionsData: ActionGridData[] =
        allActionPlanActions?.map((actionPlanAction: ActionPlanActionList) => {
            const status = actionPlanAction.type || "planifié";
            const categories = actionPlanAction.action.csrThemes.flatMap((theme) => {
                const parent = theme.parent;
                if (parent === undefined) return [];

                return [
                    {
                        id: parent.id,
                        title: parent.title,
                    },
                ];
            });

            const totalImpact = actionPlanAction.totalImpact;
            const impact = !Array.isArray(totalImpact) ? totalImpact[scoreId] : null;
            const impactsByScore = scoresEnabled.reduce<Record<number, number | null>>((acc, score) => {
                if (Array.isArray(totalImpact)) {
                    acc[score.id] = null;
                    return acc;
                }
                const value = totalImpact[score.id] ?? totalImpact[score.id.toString()];
                acc[score.id] = value ?? null;
                return acc;
            }, {});

            return {
                id: actionPlanAction.id,
                actionId: actionPlanAction.actionId,
                order: actionPlanAction.order,
                title: actionPlanAction.action.title,
                categories: categories,
                startYear: actionPlanAction.startSimulationSsqPost.campaign?.label || "-",
                endYear: actionPlanAction.endSimulationSsqPost.campaign?.label || "-",
                status: status,
                impact: impact ?? null,
                impactsByScore,
                unit: lastSelectedVariation.unit,
                canEdit: true,
                canDelete: allowDelete,
                isEngaged: true, // Toutes les actions affichées sont engagées
            };
        }) ?? [];

    // Configuration de pagination basée sur le nombre d'actions
    const shouldEnablePagination = actionsData.length > 20;
    const pageSize = shouldEnablePagination ? 20 : actionsData.length;

    const totalImpactsByScore = scoresEnabled.reduce<Record<number, number>>((acc, score) => {
        acc[score.id] = actionsData.reduce((sum, action) => {
            const value = action.impactsByScore[score.id];
            if (value !== null && !isNaN(value)) {
                return sum + value;
            }
            return sum;
        }, 0);
        return acc;
    }, {});
    const selectedScoreTotalImpact = totalImpactsByScore[scoreId] ?? 0;

    // État de chargement combiné : legacy actions + API
    const isLoading =
        allActionPlanActions === undefined || areActionsReady === false || !actionPlanId || !scoreId || isQueryLoading;

    // Handlers for mobile card actions
    const handleEdit = (actionId: string) => {
        navigate(`${actionId}`);
    };

    const handleDelete = (actionId: string) => {
        if (!allowDelete) return;
        if (!actionPlanId) return;

        const confirmed = window.confirm(
            t(
                "actionplan.delete_confirmation",
                "Are you sure you want to delete this action? This action cannot be undone.",
            ),
        );

        if (confirmed) {
            deleteActionMutation.mutate({
                actionPlanId,
                actionPlanActionId: actionId,
            });
        }
    };

    // Render mobile cards
    if (isMobile) {
        return (
            <Box sx={{ padding: theme.spacing(2) }}>
                <Stack spacing={0}>
                    {isLoading ?
                        <Typography variant="body" sx={{ textAlign: "center", padding: theme.spacing(4) }}>
                            {t("common.loading", "Loading...")}
                        </Typography>
                    : actionsData.length === 0 ?
                        <Typography
                            variant="body"
                            sx={{
                                textAlign: "center",
                                padding: theme.spacing(4),
                                color: theme.custom.grey[1000],
                                fontStyle: "italic",
                            }}
                        >
                            {t("actionplan.no_records", "No records to display")}
                        </Typography>
                    :   actionsData.map((action) => (
                            <ActionCard
                                key={action.id}
                                action={action}
                                onEdit={handleEdit}
                                onDelete={allowDelete ? handleDelete : undefined}
                            />
                        ))
                    }
                </Stack>

                {/* Total Section for Mobile */}
                {!isLoading && actionsData.length > 0 && (
                    <Box
                        sx={{
                            backgroundColor: "#FFFFFF",
                            padding: theme.spacing(2),
                            marginTop: theme.spacing(2),
                            borderRadius: "12px",
                            border: `1px solid ${theme.custom.grey[500]}`,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Typography variant="body" sx={{ fontWeight: 700 }}>
                            {t("actionplan.total_impact", {
                                defaultValue: "Impact total ({{actionsDataLength}} actions engagées)",
                                actionsDataLength: actionsData.length,
                            })}
                        </Typography>
                        <ApproximateImpactTooltip
                            value={selectedScoreTotalImpact}
                            precision={selectedScorePrecision}
                            unit={lastSelectedVariation.unit}
                        >
                            <NumberWithUnit
                                value={formatScore(selectedScoreTotalImpact)}
                                unit={lastSelectedVariation.unit}
                                size={"small"}
                                colorOverride={getColor(selectedScoreTotalImpact)}
                            />
                        </ApproximateImpactTooltip>
                    </Box>
                )}
            </Box>
        );
    }

    // Render desktop table
    return (
        <>
            <div
                style={{
                    backgroundColor: "#FFFFFF",
                    borderRadius: "12px",
                    overflow: "hidden",
                }}
            >
                <MRTGrid
                    columns={gridColumns.mrt_columns}
                    data={actionsData}
                    enableRowSelection={false}
                    enablePagination={shouldEnablePagination}
                    enableSelectAll={false}
                    enableColumnPinning={true}
                    enableSorting={!isPdfMode}
                    enableTopToolbar={!isPdfMode}
                    enableGlobalFilter={!isPdfMode}
                    muiTableContainerProps={{
                        sx: {
                            backgroundColor: "#FFFFFF",
                            border: "none",
                            borderRadius: "0",
                        },
                    }}
                    muiTopToolbarProps={{
                        sx: {
                            backgroundColor: "#FFFFFF",
                            borderBottom: "none",
                            padding: "12px 20px",
                        },
                    }}
                    muiTableBodyCellProps={{
                        // Style personnalisé pour les cellules - enlever les bordures verticales
                        sx: (theme) => ({
                            padding: "0 20px",
                            color: theme.palette.primary[700],
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            borderBottom: `1px solid ${theme.custom.grey[500]}`,
                            borderLeft: "none",
                            borderRight: "none",
                            height: "auto",
                        }),
                    }}
                    muiTableBodyRowProps={{
                        sx: {
                            "& td": {
                                paddingTop: "0.5rem",
                                paddingBottom: "0.5rem",
                            },
                        },
                    }}
                    muiTableHeadCellProps={{
                        sx: (theme) => ({
                            fontWeight: "600",
                            fontSize: "0.875rem",
                            color: theme.custom.grey[2000],
                            backgroundColor: "#FEFCFB",
                            borderBottom: `1px solid ${theme.custom.grey[500]}`,
                            borderTop: "none",
                            borderLeft: "none",
                            borderRight: "none",
                            textOverflow: "ellipsis",
                            minHeight: theme.spacing(6.5),
                            alignItems: "center",
                            justifyContent: "center",
                            alignSelf: "stretch",
                            paddingLeft: "20px",
                            "&>.Mui-TableHeadCell-Content>.Mui-TableHeadCell-Content-Labels": {
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%",
                                "&>.Mui-TableHeadCell-Content-Wrapper": {
                                    whiteSpace: "normal",
                                },
                            },
                            "&>.Mui-TableHeadCell-Content .MuiDivider-vertical": {
                                opacity: "0",
                            },
                        }),
                    }}
                    state={{
                        isLoading: isLoading,
                        columnVisibility: gridColumns.columnVisibility,
                        columnOrder: gridColumns.columnOrder,
                        columnSizing: gridColumns.columnSizing,
                        columnPinning: {
                            left: [
                                "order",
                                gridColumns.columnOrder.find(
                                    (id) => id !== "order" && gridColumns.columnVisibility[id] === true,
                                ) ?? gridColumns.columnOrder[0],
                            ].filter((id) => gridColumns.columnVisibility[id] === true),
                        },
                    }}
                    onColumnSizingChange={gridColumns.setColumnSizing}
                    initialState={{
                        isLoading: true,
                        pagination: !isLoading ? { pageIndex: 0, pageSize: pageSize } : undefined,
                    }}
                />

                {/* Total Section */}
                <div
                    style={{
                        backgroundColor: "#FFFFFF",
                        padding: "16px 20px",
                        borderTop: `1px solid #E5E7EB`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        overflowX: "auto",
                        gap: "24px",
                    }}
                >
                    <span style={{ fontWeight: 700, fontSize: "0.875rem", flexShrink: 0 }}>
                        {t("actionplan.total_impact", {
                            defaultValue: "Total impact ({actionsDataLength} engaged actions)",
                            actionsDataLength: actionsData.length,
                        })}
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: "16px", marginLeft: "auto" }}>
                        {scoresEnabled.map((score) => {
                            const unit =
                                scoresWithVariations.find((row) => row.scoreId === score.id)?.unit ??
                                score.unit?.abbreviation ??
                                lastSelectedVariation.unit;
                            const precision = getScorePrecision(score);
                            const scoreTotalImpact = totalImpactsByScore[score.id] ?? 0;
                            return (
                                <div
                                    key={score.id}
                                    style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}
                                >
                                    <span style={{ fontSize: "0.75rem", color: "#6B7280" }}>{score.title}</span>
                                    <Suspense isLoading={isLoading || !isFetched} size={24}>
                                        <ApproximateImpactTooltip
                                            value={scoreTotalImpact}
                                            precision={precision}
                                            unit={unit}
                                        >
                                            <NumberWithUnit
                                                value={formatScore(scoreTotalImpact, { precision })}
                                                unit={unit}
                                                size={"small"}
                                                colorOverride={getColor(scoreTotalImpact, {
                                                    improvement: score.improvement,
                                                    precision,
                                                })}
                                            />
                                        </ApproximateImpactTooltip>
                                    </Suspense>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
};
